import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Stack,
  Image,
  SimpleGrid,
  Container,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import { ArrowDownIcon } from "@chakra-ui/icons";

/* =========================
   SECTION WRAPPER
========================= */
const Section = ({ id, bg, children }) => {
  return (
    <Box id={id} bg={bg} py="24" px="6">
      <Container maxW="6xl">{children}</Container>
    </Box>
  );
};

export default function CulinaryAIPage() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImg, setSelectedImg] = useState(null);

  useEffect(() => {
    document.title = "Culinary AI - Smart Recipe Generator";
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const images = ["r1.jpg", "r2.jpg", "r3.jpg", "r4.jpg", "r5.jpg", "r6.jpg"];

  const openImage = (img) => {
    setSelectedImg(img);
    onOpen();
  };

  return (
    <Box fontFamily="'Poppins', sans-serif" bg="#0f1020" color="white">
      {/* =========================
          HEADER
      ========================= */}
      <Flex
        position="sticky"
        top="0"
        zIndex="10"
        bg="rgba(15,16,32,0.85)"
        backdropFilter="blur(14px)"
        px="8"
        py="4"
        justify="space-between"
        align="center"
        borderBottom="1px solid rgba(239,54,75,0.25)"
      >
        <Flex align="center" gap="3">
          <Image
            src={require("../images/culinary/culinary-logo.png")}
            boxSize="32px"
          />
          <Heading size="sm">Culinary AI</Heading>
        </Flex>

        <Stack direction="row" spacing="6" fontSize="sm">
          <Text cursor="pointer" onClick={() => scrollTo("home")}>
            Home
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("features")}>
            Features
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("screenshots")}>
            Gallery
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("pricing")}>
            Pricing
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("faq")}>
            FAQ
          </Text>
        </Stack>

        <Button
          as="a"
          href="https://play.google.com/store/apps/details?id=com.aesthetic.culinary"
          target="_blank"
          bg="#EF364B"
          color="white"
          _hover={{ bg: "#ff4f67" }}
        >
          <ArrowDownIcon fontSize={20} marginRight={2} alignSelf={"center"} />{" "}
          Download Now
        </Button>
      </Flex>

      {/* =========================
          HERO (CHATGPT STYLE RESTORED)
      ========================= */}
      <Section id="home" bg="linear-gradient(135deg, #710FA8, #0f1020)">
        <Flex direction={{ base: "column", md: "row" }} align="center" gap="10">
          {/* LEFT */}
          <Stack spacing="6" maxW="lg">
            <Heading fontSize={{ base: "3xl", md: "5xl" }}>
              What can I cook today?
            </Heading>

            <Text opacity="0.8">
              AI creates recipes from your ingredients instantly. Google login
              required for all users.
            </Text>

            {/* 🔥 CHATGPT STYLE INPUT (RESTORED) */}
            <Flex
              bg="whiteAlpha.200"
              p="3"
              borderRadius="xl"
              border="1px solid rgba(239,54,75,0.4)"
              gap="3"
            >
              <input
                placeholder="e.g. chicken, rice, tomato..."
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "white",
                }}
              />

              <Button bg="#EF364B" color="white">
                Generate
              </Button>
            </Flex>

            <Text fontSize="xs" opacity="0.6">
              Try: “eggs, cheese, bread”
            </Text>

            <Stack direction="row" spacing="4" mt="4" flexWrap="wrap">
              <Button
                as="a"
                href="https://youtube.com/shorts/-TYWGbRtMHc"
                target="_blank"
                variant="ghost"
                color="white"
                px="6"
                borderRadius="full"
                border="1px solid rgba(255,255,255,0.3)"
                _hover={{
                  bg: "whiteAlpha.100",
                  transform: "translateY(-1px)",
                }}
                transition="0.2s"
              >
                ▶ Watch Demo
              </Button>

              <Button
                as="a"
                href="https://play.google.com/store/apps/details?id=com.aesthetic.culinary"
                target="_blank"
                bg="#EF364B"
                color="white"
                px="6"
                borderRadius="full"
                _hover={{
                  bg: "#ff4f67",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 4px rgba(239,54,75,0.45)",
                }}
                transition="0.2s"
              >
                Get App
              </Button>
            </Stack>
          </Stack>

          {/* RIGHT VIDEO */}
          <Flex justify={"center"} flex={1}>
            <Box
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="0 20px 60px rgba(0,0,0,0.4)"
              border="1px solid rgba(239,54,75,0.25)"
            >
              <video
                src={require("../images/culinary/hero.mp4")}
                autoPlay
                muted
                loop
                playsInline
                style={{
                  width: "280px",
                  height: "520px",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Flex>
        </Flex>
      </Section>

      {/* =========================
          FEATURES
      ========================= */}
      <Section id="features" bg="#15162a">
        <Heading textAlign="center" mb="10" color="#EF364B">
          AI Features
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="8">
          <Box bg="#1c1d33" p="6" borderRadius="xl">
            <Heading size="md">Ingredient → Recipe</Heading>
            <Text opacity="0.7">Turn fridge items into meals.</Text>
          </Box>

          <Box bg="#1c1d33" p="6" borderRadius="xl">
            <Heading size="md">Camera Scan</Heading>
            <Text opacity="0.7">Detect food from images.</Text>
          </Box>

          <Box bg="#1c1d33" p="6" borderRadius="xl">
            <Heading size="md">Diet AI</Heading>
            <Text opacity="0.7">Keto, vegan, custom plans.</Text>
          </Box>
        </SimpleGrid>
      </Section>

      {/* =========================
          GALLERY (HORIZONTAL FIXED HEIGHT)
      ========================= */}
      <Section id="screenshots" bg="#0f1020">
        <Heading textAlign="center" mb="10" color="#710FA8">
          App Screenshots
        </Heading>

        {/* 6 GRID, NO SCROLL */}
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing="4">
          {images.slice(0, 6).map((img, i) => (
            <Image
              key={i}
              src={require(`../images/culinary/${img}`)}
              height="220px"
              width="100%"
              objectFit="cover"
              borderRadius="lg"
              cursor="pointer"
              onClick={() => openImage(img)}
              transition="0.2s"
              _hover={{ transform: "scale(1.03)" }}
            />
          ))}
        </SimpleGrid>

        {/* FULLSCREEN MODAL FIXED */}
        <Modal isOpen={isOpen} onClose={onClose} size="full">
          <ModalOverlay />

          <ModalContent bg="black">
            <ModalBody
              display="flex"
              alignItems="center"
              justifyContent="center"
              p="0"
            >
              {selectedImg && (
                <Image
                  src={require(`../images/culinary/${selectedImg}`)}
                  maxH="95vh"
                  maxW="95vw"
                  objectFit="contain"
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Section>

      {/* =========================
          PRICING (REGION BASED + LOGIN NOTE)
      ========================= */}
      <Section id="pricing" bg="#15162a">
        <Heading textAlign="center" mb="10" color="#EF364B">
          Pricing
        </Heading>

        <Text textAlign="center" mb="6" opacity="0.7">
          Prices may vary by region. Google account login is required for all
          plans.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="8">
          {/* FREE */}
          <Box bg="#1c1d33" p="6" borderRadius="xl">
            <Heading size="md">Free</Heading>

            <Text fontSize="2xl" mt="2" color="#EF364B">
              $0
            </Text>

            <Text opacity="0.7">Limited daily recipes</Text>
          </Box>

          {/* PRO */}
          <Box bg="#EF364B" p="6" borderRadius="xl" position="relative">
            {/* BADGE */}
            <Box
              position="absolute"
              top="10px"
              right="10px"
              bg="white"
              color="#EF364B"
              fontSize="xs"
              px="2"
              py="1"
              borderRadius="md"
              fontWeight="bold"
            >
              7 DAYS FREE
            </Box>

            <Heading size="md">Pro</Heading>

            <Text fontSize="2xl" mt="2" color="white">
              $1 / month
            </Text>

            <Text opacity="0.9">Unlimited AI recipes + camera scan</Text>

            <Text fontSize="xs" mt="2" opacity="0.8">
              Cancel anytime after free trial
            </Text>
          </Box>
        </SimpleGrid>
      </Section>

      {/* =========================
          FAQ (10 QUESTIONS)
      ========================= */}
      <Section id="faq" bg="#0f1020">
        <Heading textAlign="center" mb="10" color="#710FA8">
          FAQ
        </Heading>

        <Accordion allowToggle>
          {[
            {
              q: "How does AI generate recipes?",
              a: "Culinary AI analyzes your ingredients, dietary preferences, and cooking patterns to generate personalized recipes instantly.",
            },
            {
              q: "Can I use fridge leftovers?",
              a: "Yes. You can input leftover ingredients and the AI will suggest complete meals based on what you already have.",
            },
            {
              q: "Is Culinary AI free?",
              a: "Yes, there is a free version with limited daily usage. Full features require a subscription.",
            },
            {
              q: "Do I need internet?",
              a: "Yes. Internet is required for AI processing, authentication, and syncing your recipes.",
            },
            {
              q: "Can I scan food with camera?",
              a: "Yes. You can take a photo of ingredients and the AI will identify them and suggest recipes.",
            },
            {
              q: "Does it support diets?",
              a: "Yes. You can choose keto, vegan, high-protein, and custom diet preferences.",
            },
            {
              q: "Is Google login required?",
              a: "Yes. All users must sign in with Google to save recipes and sync data across devices.",
            },
            {
              q: "Can I save recipes?",
              a: "Yes. You can save, favorite, and revisit all generated recipes anytime.",
            },
            {
              q: "Does it work offline?",
              a: "No. The app requires internet connection for AI generation and syncing.",
            },
            {
              q: "Is there subscription pricing?",
              a: "Yes. Premium features are available via monthly subscription. Pricing may vary by region.",
            },
          ].map((item, i) => (
            <AccordionItem border="none" key={i}>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  {item.q}
                </Box>
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel opacity="0.8">{item.a}</AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* =========================
          FOOTER
      ========================= */}
      <Box py="10" textAlign="center" opacity="0.5">
        © {new Date().getFullYear()} Culinary AI
      </Box>
    </Box>
  );
}
